import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getUserClinicsByUserId } from 'app/services/user';

const ClinicContext = createContext();

export function ClinicProvider({ children }) {
  const [selectedClinicId, setSelectedClinicId] = useState(
    localStorage.getItem('selected_clinic_id') ?? null
  );

  const user = useSelector(({ auth }) => auth.user);
  const userId = user?._id;

  const { data: clinics = [] } = useQuery({
    queryKey: ['userClinics', userId],
    queryFn: () => getUserClinicsByUserId({ userId }),
    enabled: Boolean(userId),
    meta: {
      errorMessage: 'Failed to fetch clinics',
    },
  });

  useEffect(() => {
    if (clinics.length) {
      const savedClinicId = localStorage.getItem('selected_clinic_id');
      const validSavedClinic = clinics.find((clinic) => clinic.id === savedClinicId);
      const newSelectedId = validSavedClinic?.id || clinics[0].id;
      setSelectedClinicId(newSelectedId);
      localStorage.setItem('selected_clinic_id', newSelectedId);
    }
  }, [clinics]);

  const handleClinicChange = (newClinicId) => {
    setSelectedClinicId(newClinicId);
    localStorage.setItem('selected_clinic_id', newClinicId);
  };

  const selectedClinic = useMemo(
    () => clinics.find((clinic) => clinic.id === selectedClinicId),
    [clinics, selectedClinicId]
  );

  return (
    <ClinicContext.Provider
      value={{
        selectedClinicId,
        setSelectedClinicId: handleClinicChange,
        selectedClinic,
        clinics,
      }}
    >
      {children}
    </ClinicContext.Provider>
  );
}

export function useClinicContext() {
  return useContext(ClinicContext);
}
